import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { VNFieldHelpHeading } from '@/components/VNFieldHelp';

import VNSwitch from '../VNSwitch/VNSwitch';

type Props = VNFieldHelpProps & {
  title?: string | React.ReactNode;
  actions?: React.ReactNode;
  main?: boolean;
  description?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  enableSwitch?: string;
  disabledSwitch?: boolean;
  subPortlet?: boolean;
  /** Show a chevron that collapses/expands the body. Uses local UI state — not form-bound. */
  collapsible?: boolean;
  /** Initial collapsed state when `collapsible` is set (default expanded). */
  defaultCollapsed?: boolean;
};

const VNPortlet = ({
  title = '',
  actions,
  main = false,
  description = '',
  className = '',
  style,
  children,
  enableSwitch,
  disabledSwitch = false,
  subPortlet = false,
  collapsible = false,
  defaultCollapsed = false,
  ...fieldHelpProps
}: Props) => {
  const methods = useFormContext();
  const watch = methods != null ? methods.watch : null;
  const isEnabled = watch != null && enableSwitch ? watch(enableSwitch) : true;
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const showBody = isEnabled && !(collapsible && collapsed);

  return (
    <div
      className={`vn-portlet ${className} ${isEnabled ? 'portlet-enabled' : ''} ${subPortlet ? 'sub-portlet' : ''} ${
        collapsible && collapsed ? 'portlet-collapsed' : ''
      }`}
      style={style}
    >
      {!main && title && (
        <div className="portlet-header">
          <div className="portlet-title">
            <VNFieldHelpHeading title={title} {...fieldHelpProps} />
            {description && <h3>{description}</h3>}
          </div>
          {actions && <div className="portlet-actions">{actions}</div>}
          {enableSwitch && (
            <div className="portlet-actions">
              <VNSwitch name={enableSwitch} label="" disabled={disabledSwitch} />
            </div>
          )}
          {collapsible && (
            <IconButton
              type="button"
              className="portlet-collapse-toggle"
              size="small"
              onClick={() => setCollapsed((prev) => !prev)}
              aria-label={collapsed ? 'Expand section' : 'Collapse section'}
              aria-expanded={!collapsed}
            >
              {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </IconButton>
          )}
        </div>
      )}
      {main && (
        <div className="main-title">
          <VNFieldHelpHeading title={title} {...fieldHelpProps} />
        </div>
      )}
      {showBody && <div className="portlet-container">{children}</div>}
    </div>
  );
};

export default VNPortlet;
